/* =========================================================
   Gameplay navigation / content groups
========================================================= */

const gameplayTabs = Array.from(
  document.querySelectorAll('[data-gameplay-tab]'),
);

const gameplayGroups = Array.from(
  document.querySelectorAll('[data-gameplay-group]'),
);

const gameplaySections = Array.from(
  document.querySelectorAll('[data-gameplay-section]'),
);

const gameplayNavSticky = document.querySelector('.gameplay-nav-sticky');

/* =========================================================
   Helpers
========================================================= */

function getGameplayGroupFromTab(tab) {
  if (!tab) {
    return null;
  }

  return tab.closest('.gameplay-nav__row--primary') ? 'primary' : 'secondary';
}

function getGameplaySectionIdFromTab(tab) {
  return tab?.getAttribute('href')?.replace('#', '') ?? null;
}

/* =========================================================
   Background switching
========================================================= */

function updateGameplayBackground(groupName) {
  document
    .querySelectorAll('[data-gameplay-background]')
    .forEach((background) => {
      const isActive = background.dataset.gameplayBackground === groupName;

      background.classList.toggle('is-active', isActive);
    });
}

/* =========================================================
   Active navigation button
========================================================= */

function setActiveGameplayTab(sectionId) {
  gameplayTabs.forEach((tab) => {
    const isActive = tab.getAttribute('href') === `#${sectionId}`;

    tab.classList.toggle('is-active', isActive);

    if (isActive) {
      tab.setAttribute('aria-current', 'page');
    } else {
      tab.removeAttribute('aria-current');
    }
  });
}

/* =========================================================
   Show primary / secondary gameplay group
========================================================= */

function showGameplayGroup(groupName) {
  gameplayGroups.forEach((group) => {
    const isActive = group.dataset.gameplayGroup === groupName;

    group.hidden = !isActive;

    group.classList.toggle('is-active', isActive);
  });

  updateGameplayBackground(groupName);
}

/* =========================================================
   Scroll to gameplay section
========================================================= */

function scrollToGameplaySection(sectionId, smooth = true) {
  const section = document.getElementById(sectionId);

  if (!section) {
    console.warn(`No gameplay section found with id="${sectionId}"`);

    return;
  }

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  section.scrollIntoView({
    behavior: smooth && !reducedMotion ? 'smooth' : 'auto',

    block: 'start',
  });
}

/* =========================================================
   Activate gameplay destination
========================================================= */

function activateGameplaySection(
  selectedTab,
  { scroll = true, updateUrl = true, smooth = true } = {},
) {
  if (!selectedTab) {
    return;
  }

  const targetId = getGameplaySectionIdFromTab(selectedTab);

  const groupName = getGameplayGroupFromTab(selectedTab);

  if (!targetId || !groupName) {
    return;
  }

  const targetSection = document.getElementById(targetId);

  if (!targetSection) {
    console.warn(`No gameplay section found with id="${targetId}"`);

    return;
  }

  /* =====================================
     Show correct content group
  ===================================== */

  showGameplayGroup(groupName);

  /* =====================================
     Update selected navigation button
  ===================================== */

  setActiveGameplayTab(targetId);

  /* =====================================
     Update URL
  ===================================== */

  if (updateUrl) {
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}#${targetId}`,
    );
  }

  /* =====================================
     Scroll after group is visible
  ===================================== */

  if (scroll) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToGameplaySection(targetId, smooth);
      });
    });
  }
}

/* =========================================================
   Gameplay tab clicks
========================================================= */

gameplayTabs.forEach((tab) => {
  tab.addEventListener('click', (event) => {
    event.preventDefault();

    activateGameplaySection(tab, {
      scroll: true,
      updateUrl: true,
      smooth: true,
    });
  });
});

/* =========================================================
   Sticky gameplay navigation state
========================================================= */

function updateGameplayNavStickyState() {
  if (!gameplayNavSticky) {
    return;
  }

  /*
   * Read the actual CSS sticky top value.
   *
   * Desktop:
   * top: 1rem
   *
   * Mobile:
   * top: 0.5rem
   */
  const computedStyle = window.getComputedStyle(gameplayNavSticky);

  const stickyTop = parseFloat(computedStyle.top) || 0;

  const rect = gameplayNavSticky.getBoundingClientRect();

  const isStuck = rect.top <= stickyTop + 1;

  gameplayNavSticky.classList.toggle('is-stuck', isStuck);
}

if (gameplayNavSticky) {
  window.addEventListener('scroll', updateGameplayNavStickyState, {
    passive: true,
  });

  window.addEventListener('resize', updateGameplayNavStickyState);

  updateGameplayNavStickyState();
}

/* =========================================================
   Scroll-aware active navigation
========================================================= */

const gameplaySectionObserver = new IntersectionObserver(
  (entries) => {
    const visibleEntries = entries
      .filter((entry) => entry.isIntersecting)
      .sort(
        (a, b) =>
          Math.abs(a.boundingClientRect.top) -
          Math.abs(b.boundingClientRect.top),
      );

    if (!visibleEntries.length) {
      return;
    }

    const activeSection = visibleEntries[0].target;

    if (!activeSection.id) {
      return;
    }

    setActiveGameplayTab(activeSection.id);

    /*
     * Keep the URL synced as
     * the user manually scrolls.
     */
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}#${activeSection.id}`,
    );
  },
  {
    root: null,

    /*
     * Active zone near the upper
     * part of the viewport.
     *
     * We can fine-tune this later
     * once the final sticky-nav
     * dimensions are established.
     */
    rootMargin: '-18% 0px -68% 0px',

    threshold: 0,
  },
);

gameplaySections.forEach((section) => {
  gameplaySectionObserver.observe(section);
});

/* =========================================================
   Initial gameplay section
========================================================= */

const initialTargetId = window.location.hash.replace('#', '');

const initialTab =
  gameplayTabs.find(
    (tab) => tab.getAttribute('href') === `#${initialTargetId}`,
  ) ?? gameplayTabs[0];

if (initialTab) {
  activateGameplaySection(initialTab, {
    /*
     * If the URL contains a hash,
     * go directly to that section.
     *
     * Otherwise leave the page
     * at its normal starting position.
     */
    scroll: Boolean(initialTargetId),

    updateUrl: Boolean(initialTargetId),

    smooth: false,
  });
}

/* =========================================================
   Browser hash changes
========================================================= */

window.addEventListener('hashchange', () => {
  const targetId = window.location.hash.replace('#', '');

  const targetTab = gameplayTabs.find(
    (tab) => tab.getAttribute('href') === `#${targetId}`,
  );

  if (!targetTab) {
    return;
  }

  activateGameplaySection(targetTab, {
    scroll: true,
    updateUrl: false,
    smooth: true,
  });
});
/* =========================================================
   Archives carousel
========================================================= */

const gameplayCarousels = document.querySelectorAll('[data-gameplay-carousel]');

gameplayCarousels.forEach((carousel) => {
  const track = carousel.querySelector('[data-carousel-track]');
  const slides = Array.from(carousel.querySelectorAll('[data-carousel-slide]'));
  const previousButton = carousel.querySelector('[data-carousel-previous]');
  const nextButton = carousel.querySelector('[data-carousel-next]');

  if (!track || !previousButton || !nextButton || slides.length === 0) {
    return;
  }

  let currentIndex = 0;

  const updateCarousel = () => {
    track.style.transform = `translateX(-${currentIndex * 100}%)`;

    slides.forEach((slide, index) => {
      const isActive = index === currentIndex;

      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });

    previousButton.disabled = currentIndex === 0;
    nextButton.disabled = currentIndex === slides.length - 1;
  };

  const showPreviousSlide = () => {
    if (currentIndex === 0) {
      return;
    }

    currentIndex -= 1;
    updateCarousel();
  };

  const showNextSlide = () => {
    if (currentIndex === slides.length - 1) {
      return;
    }

    currentIndex += 1;
    updateCarousel();
  };

  previousButton.addEventListener('click', showPreviousSlide);
  nextButton.addEventListener('click', showNextSlide);

  carousel.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showPreviousSlide();
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      showNextSlide();
    }
  });

  updateCarousel();
});

/* =========================================
   Resume gameplay videos after iOS sleep
========================================= */

function resumeGameplayVideos() {
  if (document.visibilityState !== 'visible') {
    return;
  }

  const videos = document.querySelectorAll(
    '.gameplay-content-group.is-active video[autoplay]',
  );

  videos.forEach((video) => {
    if (!video.paused && !video.ended) {
      return;
    }

    video.muted = true;

    const playPromise = video.play();

    if (playPromise) {
      playPromise.catch(() => {
        // Safari may still be restoring its media session.
      });
    }
  });
}

document.addEventListener('visibilitychange', resumeGameplayVideos);

window.addEventListener('pageshow', resumeGameplayVideos);
